import { Typography, Box, Divider, Alert, Button } from "@mui/material";
import { CalendarToday as CalendarIcon, Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import DataContainer, { TableActionButton, TableActionGroup } from "../components/common/DataContainer";
import { useSearchFilter } from "../hooks/useSearchFilter";
import { useToast } from "../components/common/custom-component/ToastProvider";
import { useDispatch, useSelector } from "react-redux";

import EnhancedConfirmationDialog from "../components/common/custom-component/EnhancedConfirmationDialog";
import SearchFilterBar from "../components/common/SearchFilterBar";
import CustomPagination from "../components/common/CustomPagination";
import Loader from "../components/common/custom-component/Loader";
import { logger } from "helpers/logger";

import { manageTemplatesReducer, initialState } from "../reducers/manageTemplatesReducer";
import { useTableColumns } from "../hooks/useTableColumns";

import { fetchTemplatesRequest } from "../store/slices/database/templatesSlice";

import {
    addTemplateThunk,
    deleteTemplateThunk,
    deleteTemplateVersionThunk,
    downloadTemplateThunk,
    getTemplateUsageThunk,
    setTemplateVersionInUseThunk,
} from "../store/thunks/mdsThunks";

export default function ManageTemplatesPage() {
    const dispatch = useDispatch();
    const [t] = useTranslation();
    const { success: ToastSuccess, error: ToastError, warning: ToastWarning } = useToast();

    // One selector per value: a selector returning a new object would re-render the page on every
    // store change anywhere in the app.
    const templatesData = useSelector((state) => state.templates?.data?.data);
    const loading = useSelector((state) => state.templates?.loading);
    const error = useSelector((state) => state.templates?.error);

    const [deletedRecordIds, setDeletedRecordIds] = useState(new Set());

    const [state, dispatchState] = useReducer(manageTemplatesReducer, initialState);

    const activeThunksRef = useRef([]);

    // One row per template version. deletedRecordIds hides a deleted row at once, before the refetch
    // (or when it can't run, offline). versionLabel is what the Version column shows and search matches.
    const displayData = useMemo(
        () =>
            (templatesData || [])
                .filter((r) => !deletedRecordIds.has(r.templateId))
                .map((r) => ({
                    ...r,
                    versionLabel: r.isInUse ? `v${r.version} · ${t("In use")}` : `v${r.version}`,
                })),
        [templatesData, deletedRecordIds, t]
    );

    useEffect(() => {
        dispatch(fetchTemplatesRequest());
    }, [dispatch]);

    // A successful refetch is the server's truth: the rows hidden since a delete are no longer needed
    // (a deleted row isn't in it). Offline the refetch fails, the list doesn't change, rows stay hidden.
    useEffect(() => {
        setDeletedRecordIds((prev) => (prev.size ? new Set() : prev));
    }, [templatesData]);

    useEffect(() => {
        return () => {
            activeThunksRef.current.forEach((thunk) => {
                if (thunk && typeof thunk.abort === "function") thunk.abort();
            });
            activeThunksRef.current = [];
        };
    }, []);

    // #region SEARCH & PAGINATION
    const { setSearchQuery, filteredData } = useSearchFilter(displayData, ["templateName", "extension", "versionLabel"], null);

    const totalPages = Math.max(1, Math.ceil((filteredData?.length || 0) / state.table.recordsPerPage));

    // Fewer pages than the current one (after a delete or a narrower search): go to the last page.
    useEffect(() => {
        if (state.table.currentPage > totalPages) {
            dispatchState({ type: "SET_CURRENT_PAGE", payload: totalPages });
        }
    }, [state.table.currentPage, totalPages]);

    const pagedData = useMemo(() => {
        const start = (state.table.currentPage - 1) * state.table.recordsPerPage;
        const end = start + state.table.recordsPerPage;
        return (filteredData || []).slice(start, end);
    }, [filteredData, state.table.currentPage, state.table.recordsPerPage]);
    // #endregion

    // #region ADD TEMPLATE
    // Same limits as the backend (FileSecurity "Templates" policy and the 100-character name).
    const MAX_TEMPLATE_NAME_LENGTH = 100;
    const TEMPLATE_ACCEPT = ".docx,.odt,.xlsx,.ods";
    const TEMPLATE_MAX_SIZE_BYTES = 5242880;

    // The name typed in the Add dialog, for the live hint under it.
    const [typedTemplateName, setTypedTemplateName] = useState("");

    // Per template name: the version in use and the highest version number (a new upload gets the next).
    const versionsByName = useMemo(() => {
        const byName = new Map();
        (templatesData || []).forEach((r) => {
            const entry = byName.get(r.templateName) || { inUse: null, max: 0 };
            if (r.isInUse) entry.inUse = r.version;
            entry.max = Math.max(entry.max, r.version);
            byName.set(r.templateName, entry);
        });
        return byName;
    }, [templatesData]);

    const typedNameVersions = versionsByName.get(typedTemplateName.trim());
    const templateNameHint = typedNameVersions
        ? t("'{{name}}' already exists (v{{inUse}} in use). A new file becomes v{{next}} and is used from then on; a file it already has changes nothing.", {
              name: typedTemplateName.trim(),
              inUse: typedNameVersions.inUse ?? typedNameVersions.max,
              next: typedNameVersions.max + 1,
          })
        : t("Using an existing template name with a new file adds a new version of that template. Uploading a file it already has changes nothing.");

    const handleAddClick = () => {
        setTypedTemplateName("");
        dispatchState({ type: "OPEN_ADD_DIALOG" });
    };

    const handleCancelAdd = useCallback(() => {
        dispatchState({ type: "CLOSE_ADD_DIALOG" });
    }, []);

    const handleConfirmAdd = useCallback(
        async (formData) => {
            const templateName = (formData?.templateName || "").trim();
            const file = formData?.templateFile;

            if (!templateName || !file) {
                ToastError(t("All fields are required"));
                return;
            }
            if (templateName.length > MAX_TEMPLATE_NAME_LENGTH) {
                ToastError(t("Template name can be at most {{max}} characters", { max: MAX_TEMPLATE_NAME_LENGTH }));
                return;
            }

            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

            try {
                // The file is sent as it is (multipart upload): no base64 conversion in the browser.
                const thunkPromise = dispatch(addTemplateThunk({ templateName, file }));

                activeThunksRef.current.push(thunkPromise);

                const result = await thunkPromise;

                activeThunksRef.current = activeThunksRef.current.filter((x) => x !== thunkPromise);

                if (addTemplateThunk.fulfilled.match(result)) {
                    const saved = result.payload?.data || {};
                    const name = saved.templateName || templateName;

                    dispatch(fetchTemplatesRequest());
                    dispatchState({ type: "CLOSE_ADD_DIALOG" });

                    if (saved.alreadyExisted) {
                        ToastWarning(t("This file is already v{{version}} of '{{name}}'. Nothing was uploaded.", { version: saved.version, name }));
                    } else if (saved.version > 1) {
                        ToastSuccess(t("New version v{{version}} of '{{name}}' saved. It is now the one used.", { version: saved.version, name }));
                    } else {
                        ToastSuccess(t("Template '{{name}}' created (v1).", { name }));
                    }
                } else if (addTemplateThunk.rejected.match(result)) {
                    // The backend says why (e.g. a refused file, or the file is another template's version in use).
                    const msg = result.payload || t("An error occured, please try again later.");
                    ToastError(msg);
                }
            } catch (e) {
                logger.error("Unexpected error in handleConfirmAdd:", e);
                ToastError(t("An unexpected error occurred"));
            } finally {
                dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
            }
        },
        [dispatch, ToastSuccess, ToastError, ToastWarning, t]
    );
    // #endregion

    // #region DELETE TEMPLATE
    // What will be deleted is decided from the server's usage of the clicked version:
    //   not in use               -> that version only
    //   in use, other versions   -> only the whole template (the version in use can't go alone)
    //   the only version         -> the whole template
    // The whole template is refused while workflow steps are linked to it.
    const handleDeleteClick = useCallback(
        async (row) => {
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
            try {
                const result = await dispatch(getTemplateUsageThunk({ templateId: row.templateId }));
                if (getTemplateUsageThunk.rejected.match(result)) {
                    ToastError(result.payload || t("An error occured, please try again later."));
                    return;
                }

                const usage = result.payload?.data || {};
                if (usage.isInUse && usage.linkedSteps?.length) {
                    ToastWarning(
                        t("Template '{{name}}' is used by {{count}} workflow step(s): {{steps}}. Remove those links first.", {
                            name: usage.templateName,
                            count: usage.linkedSteps.length,
                            steps: usage.linkedSteps.join(", "),
                        })
                    );
                    return;
                }

                const mode = !usage.isInUse ? "version" : usage.versionCount > 1 ? "inUse" : "template";
                dispatchState({ type: "OPEN_DELETE_DIALOG", payload: { record: row, mode, usage } });
            } catch (e) {
                logger.error("Unexpected error in handleDeleteClick:", e);
                ToastError(t("An unexpected error occurred"));
            } finally {
                dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
            }
        },
        [dispatch, ToastError, ToastWarning, t]
    );

    const handleCancelDelete = useCallback(() => {
        dispatchState({ type: "CLOSE_DELETE_DIALOG" });
    }, []);

    // The Delete dialog's text and button for the chosen mode.
    const deleteDialog = useMemo(() => {
        const target = state.dialogs.deleteTarget;
        if (!target) return { message: "", confirmText: t("DELETE") };

        const { mode, usage } = target;
        const vars = { name: usage.templateName, version: usage.version, count: usage.versionCount };
        const main =
            mode === "version"
                ? t("Delete v{{version}} of '{{name}}'? This can't be undone.", vars)
                : mode === "inUse"
                ? t("v{{version}} is the version in use of '{{name}}' and can't be deleted alone. Use another version first, or delete the whole template (all {{count}} versions). This can't be undone.", vars)
                : t("Delete template '{{name}}'? This can't be undone.", vars);
        const documents = usage.documentCount
            ? " " + t("{{count}} document(s) were generated with this version. They stay as they are; regenerating them will need another version.", { count: usage.documentCount })
            : "";

        return {
            message: main + documents,
            confirmText: mode === "inUse" ? t("Delete the whole template") : t("DELETE"),
        };
    }, [state.dialogs.deleteTarget, t]);

    const handleConfirmDelete = useCallback(async () => {
        const target = state.dialogs.deleteTarget;
        if (!target) return;

        const { record, mode } = target;
        const wholeTemplate = mode !== "version";
        const thunk = wholeTemplate ? deleteTemplateThunk : deleteTemplateVersionThunk;

        dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

        try {
            const thunkPromise = dispatch(thunk({ templateId: record.templateId }));

            activeThunksRef.current.push(thunkPromise);

            const result = await thunkPromise;

            activeThunksRef.current = activeThunksRef.current.filter((x) => x !== thunkPromise);

            if (thunk.fulfilled.match(result)) {
                // Hide the deleted row(s) at once (all versions for a whole template), then refetch.
                const hiddenIds = wholeTemplate
                    ? (templatesData || []).filter((r) => r.templateName === record.templateName).map((r) => r.templateId)
                    : [record.templateId];
                setDeletedRecordIds((prev) => {
                    const next = new Set(prev);
                    hiddenIds.forEach((id) => next.add(id));
                    return next;
                });

                dispatch(fetchTemplatesRequest());
                dispatchState({ type: "CLOSE_DELETE_DIALOG" });
                ToastSuccess(
                    wholeTemplate
                        ? t("Template '{{name}}' deleted.", { name: record.templateName })
                        : t("v{{version}} of '{{name}}' deleted.", { version: record.version, name: record.templateName })
                );
            } else if (thunk.rejected.match(result)) {
                const msg = result.payload || t("An error occured, please try again later.");
                ToastError(msg);
            }
        } catch (e) {
            logger.error("Unexpected error in handleConfirmDelete:", e);
            ToastError(t("An unexpected error occurred"));
        } finally {
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
        }
    }, [dispatch, ToastSuccess, ToastError, state.dialogs.deleteTarget, templatesData, t]);
    // #endregion

    // #region USE THIS VERSION
    // Makes an older version the one workflows use; nothing is deleted.
    const handleUseClick = useCallback((row) => {
        dispatchState({ type: "OPEN_USE_DIALOG", payload: row });
    }, []);

    const handleCancelUse = useCallback(() => {
        dispatchState({ type: "CLOSE_USE_DIALOG" });
    }, []);

    const handleConfirmUse = useCallback(async () => {
        const row = state.dialogs.useTarget;
        if (!row) return;

        dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

        try {
            const thunkPromise = dispatch(setTemplateVersionInUseThunk({ templateId: row.templateId }));

            activeThunksRef.current.push(thunkPromise);

            const result = await thunkPromise;

            activeThunksRef.current = activeThunksRef.current.filter((x) => x !== thunkPromise);

            if (setTemplateVersionInUseThunk.fulfilled.match(result)) {
                dispatch(fetchTemplatesRequest());
                dispatchState({ type: "CLOSE_USE_DIALOG" });
                ToastSuccess(t("v{{version}} of '{{name}}' is now the version in use.", { version: row.version, name: row.templateName }));
            } else if (setTemplateVersionInUseThunk.rejected.match(result)) {
                ToastError(result.payload || t("An error occured, please try again later."));
            }
        } catch (e) {
            logger.error("Unexpected error in handleConfirmUse:", e);
            ToastError(t("An unexpected error occurred"));
        } finally {
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
        }
    }, [dispatch, ToastSuccess, ToastError, state.dialogs.useTarget, t]);
    // #endregion

    // #region DOWNLOAD TEMPLATE
    const handleDownloadClick = useCallback(
        async (row) => {
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

            try {
                // Each row is one version: its own Carbone id (plain, no extension). The backend names the
                // file "{name}_v{version}.{ext}"; the same name is built here in case the header can't be read.
                const thunkPromise = dispatch(
                    downloadTemplateThunk({
                        templateId: row.templateId,
                        fallbackFileName: `${row.templateName}_v${row.version}.${row.extension}`,
                    })
                );

                activeThunksRef.current.push(thunkPromise);

                const result = await thunkPromise;

                activeThunksRef.current = activeThunksRef.current.filter((x) => x !== thunkPromise);

                if (downloadTemplateThunk.rejected.match(result)) {
                    const msg = result.payload || t("An error occured, please try again later.");
                    ToastError(msg);
                }
            } catch (e) {
                logger.error("Unexpected error in handleDownloadClick:", e);
                ToastError(t("An unexpected error occurred"));
            } finally {
                dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
            }
        },
        [dispatch, ToastError, t]
    );
    // #endregion

    // #region MANAGE TEMPLATES TABLE COLUMNS
    const actions = useMemo(() => ({
        onDownload: handleDownloadClick,
        onUse: handleUseClick,
        onDelete: handleDeleteClick,
    }), [handleDownloadClick, handleUseClick, handleDeleteClick]);

    const { columns, isLoading: columnsLoading } = useTableColumns('ManageTemplates', { actions });
    // #endregion

    // #region RETURN  COMPONENT
    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h3" gutterBottom color="primary">
                {t("Manage Templates")}
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
                {t("This is a Manage Templates page.")}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ my: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <TableActionGroup sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <TableActionButton text={t("Add New Template")} variant="contained" startIcon={<AddIcon />} onClick={handleAddClick} />
                </TableActionGroup>

                <SearchFilterBar
                    mode="frontend"
                    searchValue={state.table.searchInput}
                    onSearchChange={(v) => {
                        dispatchState({ type: "SET_SEARCH_INPUT", payload: v });
                        setSearchQuery(v);
                    }}
                    showFilterButton={false}
                    showSearchButton={false}
                    searchPlaceholder={t("Search templates...")}
                />
            </Box>

            {loading && !templatesData?.length ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                    <Loader variant="dots" message="Loading..." />
                </Box>
            ) : error && !displayData.length ? (
                // Nothing to show and the load failed: say so (not "no templates"), with a retry.
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={() => dispatch(fetchTemplatesRequest())}>
                            {t("Retry")}
                        </Button>
                    }
                >
                    {error}
                </Alert>
            ) : !displayData.length ? (
                <DataContainer
                    data={[]}
                    columns={columns}
                    columnsLoading={columnsLoading}
                    emptyStateIcon={CalendarIcon}
                    emptyStateTitle={t("No templates found")}
                    emptyStateSubtitle={t("Start adding templates")}
                />
            ) : (
                <>
                    <Box sx={{ p: 2 }}>
                        <DataContainer data={pagedData} columns={columns} columnsLoading={columnsLoading} showRowNumbers maxHeight={600} stickyHeader />
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <CustomPagination
                            currentPage={state.table.currentPage}
                            totalPages={totalPages}
                            onPageChange={(e, v) => dispatchState({ type: "SET_CURRENT_PAGE", payload: v })}
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                </>
            )}

            <EnhancedConfirmationDialog
                open={state.dialogs.deleteConfirm}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title={t("Confirm Delete")}
                message={deleteDialog.message}
                confirmText={deleteDialog.confirmText}
                confirmColor="error"
                cancelText={t("Cancel")}
            />

            <EnhancedConfirmationDialog
                open={state.dialogs.useConfirm}
                onClose={handleCancelUse}
                onConfirm={handleConfirmUse}
                title={t("Use this version")}
                message={
                    state.dialogs.useTarget
                        ? t("Use v{{version}} of '{{name}}'? Workflows will generate with this version from now on. Nothing is deleted.", {
                              version: state.dialogs.useTarget.version,
                              name: state.dialogs.useTarget.templateName,
                          })
                        : ""
                }
                confirmText={t("Use this version")}
                confirmColor="primary"
                cancelText={t("Cancel")}
            />

            <EnhancedConfirmationDialog
                open={state.dialogs.addConfirm}
                onClose={handleCancelAdd}
                onConfirm={handleConfirmAdd}
                title={t("Add New Template")}
                cancelText={t("Cancel")}
                confirmText={t("Confirm")}
                confirmColor="primary"
                showMessage={false}
                defaultValues={state.forms.addTemplateForm}
                fields={[
                    {
                        name: "templateName",
                        label: t("Template Name"),
                        type: "text",
                        required: true,
                        placeholder: t("Template Name"),
                        // No control characters, at most 100 characters (the backend checks the same).
                        regex: new RegExp(`^[^\u0000-\u001F\u007F]{1,${MAX_TEMPLATE_NAME_LENGTH}}$`),
                        regexErrorMessage: t("Template name can be at most {{max}} characters", { max: MAX_TEMPLATE_NAME_LENGTH }),
                        onChange: setTypedTemplateName,
                        helperText: templateNameHint,
                    },
                    {
                        name: "templateFile",
                        label: t("Upload Template"),
                        type: "file",
                        required: true,
                        accept: TEMPLATE_ACCEPT,
                        maxSizeBytes: TEMPLATE_MAX_SIZE_BYTES,
                        helperText: t("Allowed: .docx, .odt, .xlsx, .ods (max 5MB)"),
                        dropText: t("Drag & drop your template here, or click to browse"),
                        buttonText: t("Browse"),
                    },
                ]}
            />

            <LoadingBackdrop open={state.loading.isLoadingBackdrop} message={t("Loading data, please wait...")} variant="bars" color="greenGradient" />
        </Box>
    );
    // #endregion
}
